import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { ArrowRight, List, MapPin, X } from "lucide-react";
import { useLang } from "../../lib/LangContext";
import { p22Copy } from "../../lib/p22Copy";
import { LIST_ENTRIES, LOCATIONS, placeLabel, selectCity } from "../../lib/serviceAreas";
import { scrollToSection } from "../../lib/scroll";
import { Reveal } from "./Story";

const ServiceMap = lazy(() => import("./ServiceMap"));

const useInView = (ref) => {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current || seen) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setSeen(true), { rootMargin: "300px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, seen]);
  return seen;
};

const areaOf = (label) => LIST_ENTRIES.find((e) => e.label === label)?.area;

const Legend = ({ m, view, setView }) => (
  <div className="flex flex-wrap gap-2" role="group" aria-label={m.eyebrow}>
    {[["core", m.core, "bg-gold"], ["all", m.extended, "border border-gold"]].map(([k, label, dot]) => (
      <button key={k} type="button" aria-pressed={view === k} data-testid={`map-view-${k}`} onClick={() => setView(k)}
        className={`inline-flex items-center gap-2.5 px-3.5 py-2 border text-[13px] transition-colors ${view === k ? "border-gold/70 text-white bg-gold/[0.06]" : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"}`}>
        <span className={`w-2 h-2 rounded-full ${dot}`} /> {label}
      </button>
    ))}
  </div>
);

const SelectedCard = ({ m, city, onClose, onRequest }) => (
  <div data-testid="map-selected-card" className="absolute left-3 right-3 bottom-3 sm:left-4 sm:right-auto sm:bottom-4 sm:w-[300px] z-[500] bg-[#0A0A0C]/90 backdrop-blur-md border border-gold/40 p-4">
    <button type="button" aria-label="close" data-testid="map-selected-close" onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-gold"><X size={14} /></button>
    <div className="text-[11px] tracking-[0.25em] uppercase text-gold/90">{areaOf(city) === "extended" ? m.extended : m.core}</div>
    <div data-testid="map-selected-city" className="mt-1.5 font-heading text-white text-xl tracking-tight pr-6">{city}</div>
    <button type="button" data-testid="map-selected-request" onClick={onRequest}
      className="group mt-4 inline-flex items-center gap-2 bg-gold text-black px-4 py-2.5 text-[12px] tracking-[0.2em] uppercase font-semibold hover:bg-[#E5C378] transition-colors">
      {m.request} <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
    </button>
  </div>
);

export const ServiceMapChapter = () => {
  const { lang } = useLang();
  const m = p22Copy(lang).map;
  const ref = useRef(null);
  const inView = useInView(ref);
  const [selected, setSelected] = useState("");
  const [view, setView] = useState("core");
  const [showList, setShowList] = useState(false);

  const pick = (label) => { setSelected(label); if (label && areaOf(label) === "extended") setView("all"); };
  const request = () => { selectCity(selected); scrollToSection("contact", { flow: "price" }); };

  return (
    <section id="service-area" data-testid="service-map-section" className="bg-[#050505] py-20 md:py-28 border-t border-white/[0.06]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <div className="lg:col-span-4">
          <Reveal><span className="text-[12px] tracking-[0.3em] uppercase text-gold">{m.eyebrow}</span></Reveal>
          <Reveal delay={0.08} as="h2" className="mt-5 font-heading text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">{m.title}</Reveal>
          <Reveal delay={0.14} as="p" className="mt-5 text-zinc-300 text-[15px] md:text-base leading-relaxed">{m.subtitle}</Reveal>
          <Reveal delay={0.2} className="mt-8"><Legend m={m} view={view} setView={setView} /></Reveal>
          <button type="button" data-testid="map-list-toggle" aria-expanded={showList} onClick={() => setShowList((v) => !v)}
            className="mt-6 inline-flex items-center gap-2 text-[13px] text-zinc-400 hover:text-gold transition-colors">
            <List size={15} /> {showList ? m.hideList : m.list}
          </button>
        </div>
        <div className="lg:col-span-8">
          <div ref={ref} data-testid="service-map-canvas" className="relative h-[400px] sm:h-[460px] lg:h-[540px] border border-white/10 bg-[#0A0A0C] overflow-hidden">
            {inView ? (
              <Suspense fallback={<p className="p-6 text-[13px] text-zinc-500">{m.loading}</p>}>
                <ServiceMap selected={selected} onSelect={pick} view={view} baseLabel={m.base} />
              </Suspense>
            ) : <p className="p-6 text-[13px] text-zinc-500">{m.loading}</p>}
            {selected && <SelectedCard m={m} city={selected} onClose={() => setSelected("")} onRequest={request} />}
          </div>
          {showList && (
            <ul data-testid="map-city-list" aria-label={m.list} className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {LOCATIONS.map((l) => {
                const label = placeLabel(l);
                return (
                  <li key={label}>
                    <button type="button" data-testid={`map-list-${l.name}`} onClick={() => pick(label)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 border text-left text-[13px] transition-colors ${selected === label ? "border-gold text-white" : "border-white/[0.08] text-zinc-300 hover:border-white/30"}`}>
                      <MapPin size={12} className={l.area === "core" ? "text-gold" : "text-gold/50"} /> <span className="truncate">{label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};
